// Bulk employee import — talks to employee-service POST /api/v1/employees/import.
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'; // via gateway (P0-4)

function authHeader() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type FieldGroup = 'employee' | 'address' | 'emergency' | 'education';

export interface ImportField {
  key: string;
  label: string;
  required?: boolean;
  hint?: string;
  group: FieldGroup;
}

// Core employee fields + Layer-1 suffixed sub-resource columns (the common
// 1-2-of-each case). Grouped so the mapping UI stays tidy.
export const IMPORT_FIELDS: ImportField[] = [
  { key: 'email', label: 'Email', required: true, group: 'employee' },
  { key: 'first_name', label: 'First name', required: true, group: 'employee' },
  { key: 'last_name', label: 'Last name', required: true, group: 'employee' },
  { key: 'employee_id', label: 'Employee ID', hint: 'Your existing staff number', group: 'employee' },
  { key: 'position', label: 'Job title', group: 'employee' },
  { key: 'org_level', label: 'Org level', hint: 'employee / manager / director / executive / ceo', group: 'employee' },
  { key: 'department', label: 'Department', hint: 'Matched by name', group: 'employee' },
  { key: 'manager_email', label: 'Manager email', group: 'employee' },
  { key: 'hire_date', label: 'Hire date', hint: 'YYYY-MM-DD', group: 'employee' },
  { key: 'status', label: 'Status', hint: 'Active / Inactive / OnLeave / Terminated', group: 'employee' },
  { key: 'phone', label: 'Phone', hint: '+255-7XX-XXX-XXX', group: 'employee' },
  { key: 'nationality', label: 'Nationality', group: 'employee' },
  { key: 'marital_status', label: 'Marital status', hint: 'Single / Married / Divorced / Widowed', group: 'employee' },
  { key: 'national_id', label: 'National ID', hint: '20 digits', group: 'employee' },
  { key: 'nssf', label: 'NSSF', hint: '8 digits', group: 'employee' },
  { key: 'tin', label: 'TIN', hint: '9 digits', group: 'employee' },
  { key: 'driver_license', label: 'Driver license', hint: 'T + 11 digits', group: 'employee' },
  { key: 'dob', label: 'Date of birth', hint: 'YYYY-MM-DD', group: 'employee' },

  // Address (one primary, via suffixed columns)
  { key: 'address_street', label: 'Street', group: 'address' },
  { key: 'address_city', label: 'City', group: 'address' },
  { key: 'address_region', label: 'Region', group: 'address' },
  { key: 'address_postal_code', label: 'Postal code', group: 'address' },
  { key: 'address_district', label: 'District', group: 'address' },
  { key: 'address_ward', label: 'Ward', group: 'address' },

  // Emergency contacts (up to 2 via suffixed columns)
  { key: 'emergency1_name', label: 'Contact 1 name', group: 'emergency' },
  { key: 'emergency1_relationship', label: 'Contact 1 relationship', group: 'emergency' },
  { key: 'emergency1_phone', label: 'Contact 1 phone', hint: '+255-7XX-XXX-XXX', group: 'emergency' },
  { key: 'emergency2_name', label: 'Contact 2 name', group: 'emergency' },
  { key: 'emergency2_relationship', label: 'Contact 2 relationship', group: 'emergency' },
  { key: 'emergency2_phone', label: 'Contact 2 phone', hint: '+255-7XX-XXX-XXX', group: 'emergency' },

  // Education (one entry via suffixed columns)
  { key: 'education1_institution', label: 'Institution', group: 'education' },
  { key: 'education1_degree', label: 'Degree', group: 'education' },
  { key: 'education1_field', label: 'Field of study', group: 'education' },
  { key: 'education1_year', label: 'Graduation year', group: 'education' },
];

export interface RowResult {
  row: number;
  email: string | null;
  name: string | null;
  action: 'create' | 'update' | 'skip';
  errors: string[];
  warnings: string[];
}

export interface ImportSummary {
  total: number;
  to_create: number;
  to_update: number;
  skipped: number;
  addresses: number;
  emergency_contacts: number;
  education: number;
}

export interface ImportResponse {
  dry_run: boolean;
  summary: ImportSummary;
  results: RowResult[];
  child_issues: string[];
}

// Layer-2 child files (unbounded). Each row keyed by employee_email.
export interface ChildData {
  addresses?: Record<string, any>[];
  emergency_contacts?: Record<string, any>[];
  education?: Record<string, any>[];
}

export async function importEmployees(
  rows: Record<string, any>[],
  dryRun: boolean,
  child: ChildData = {},
): Promise<ImportResponse> {
  const res = await axios.post(
    `${API_URL}/employees/import`,
    {
      dry_run: dryRun,
      rows,
      addresses: child.addresses || [],
      emergency_contacts: child.emergency_contacts || [],
      education: child.education || [],
    },
    { headers: { ...authHeader() } },
  );
  return res.data;
}

// One CSV cell: RFC-4180 quoting + an Excel formula-injection guard. A value that
// starts with = + - @ (e.g. a "+255…" phone) is auto-evaluated by Excel — "+255-
// 712-345-678" becomes -1480 — so we prefix a space to keep it text. The import
// strips leading whitespace, so the value is unchanged on re-upload.
function cell(v: string): string {
  let s = v == null ? '' : String(v);
  if (/^[=+\-@]/.test(s)) s = ` ${s}`;
  if (/[",\n]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}
function csv(headers: string[], example: string[]): string {
  return `${headers.map(cell).join(',')}\n${example.map(cell).join(',')}\n`;
}

export const TEMPLATES = {
  employees: () =>
    csv(
      IMPORT_FIELDS.map((f) => f.key),
      [
        'jane.doe@company.com', 'Jane', 'Doe', 'EMP1001', 'Accountant', 'employee', 'Finance',
        'manager@company.com', '2023-02-01', 'Active', '+255-712-345-678', 'Tanzanian',
        'Single', '', '', '', '', '1992-08-20',
        '12 Uhuru St', 'Dar es Salaam', 'Dar es Salaam', '11101', 'Kinondoni', 'Msasani',
        'John Doe', 'Spouse', '+255-713-111-222', '', '', '',
        'University of Dar es Salaam', 'BSc', 'Accounting', '2014',
      ],
    ),
  addresses: () =>
    csv(
      ['employee_email', 'type', 'street', 'district', 'city', 'region', 'postal_code', 'ward', 'country'],
      ['jane.doe@company.com', 'home', '12 Uhuru St', 'Kinondoni', 'Dar es Salaam', 'Dar es Salaam', '11101', 'Msasani', 'Tanzania'],
    ),
  emergency_contacts: () =>
    csv(
      ['employee_email', 'name', 'relationship', 'phone', 'alternative_phone', 'email'],
      ['jane.doe@company.com', 'John Doe', 'Spouse', '+255-713-111-222', '', 'john@company.com'],
    ),
  education: () =>
    csv(
      ['employee_email', 'institution', 'degree', 'field', 'year', 'start_year', 'end_year', 'grade'],
      ['jane.doe@company.com', 'University of Dar es Salaam', 'BSc', 'Accounting', '2014', '2011', '2014', 'First Class'],
    ),
};
